import { Box, HStack, Input } from "@chakra-ui/react";
import { Search } from "lucide-react";
import { Spinner } from "~/components/ui/spinner";
import { MIN_SEARCH_QUERY_LENGTH } from "../constants";

interface CommandBarInputProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  query: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  /**
   * `dialog` is the size the field is in the raised palette. `hero` is the
   * size it is when the home mounts it as the thing the page is for.
   */
  size?: "dialog" | "hero";
  /** The id of the results list this field drives, while it is shown. */
  listId?: string;
  /** The id of the highlighted result, read out as the field's active option. */
  activeDescendantId?: string;
}

/**
 * The palette's search field: a combobox that owns the results list, so a
 * screen reader hears the highlighted result as the arrows move it. 16px icon
 * in Text 3, `--text-14` in the dialog, a spinner while a search is running.
 *
 * Spec: specs/design-system/ledger-shell.feature
 */
export function CommandBarInput({
  inputRef,
  query,
  onChange,
  onKeyDown,
  isLoading,
  onFocus,
  onBlur,
  placeholder = "Where would you like to go?",
  size = "dialog",
  listId,
  activeDescendantId,
}: CommandBarInputProps) {
  const hero = size === "hero";

  return (
    <HStack
      paddingX="var(--space-4)"
      paddingY={hero ? "var(--space-4)" : "var(--space-3)"}
      gap="var(--space-3)"
    >
      <Box color="var(--color-fg-3)" flexShrink={0} display="flex">
        <Search size={hero ? 20 : 16} strokeWidth={1.75} aria-hidden />
      </Box>
      <Input
        ref={inputRef}
        value={query}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-label={placeholder}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={!!listId}
        aria-controls={listId}
        aria-activedescendant={activeDescendantId}
        autoComplete="off"
        spellCheck={false}
        border="none"
        outline="none"
        boxShadow="none"
        background="transparent"
        fontSize={hero ? "var(--text-16)" : "var(--text-14)"}
        lineHeight="var(--leading-normal)"
        height="auto"
        padding={0}
        flex={1}
        minWidth={0}
        _placeholder={{ color: "var(--color-fg-placeholder)" }}
        _focus={{
          boxShadow: "none",
          outline: "none",
          background: "transparent",
        }}
      />
      {isLoading && query.length >= MIN_SEARCH_QUERY_LENGTH && (
        <Spinner size="sm" color="var(--color-fg-3)" />
      )}
    </HStack>
  );
}
